import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Accordion from '@repo/ui/accordion'
import Badge from '@repo/ui/badge'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getTripBookingsDocuments } from '../../../api/trip-folder/get-trip-bookings-documents'
import TripBookingDocumentsDataTable from '../../../components/pages/trip-bookings/trip-booking-documents-data-table'
import { transportTypeMapping } from '../../../lib/types/enums/trip/trip-type.enum'
import { TripBookingProperties } from '../../../lib/types/models/trip-booking/properties.trip-booking'
import { TripStepProperties } from '../../../lib/types/models/trip-booking/properties.trip-step'

interface FolderTripBookingDocumentsProps {
  folderId: number
}

const FolderTripBookingDocuments = ({ folderId }: FolderTripBookingDocumentsProps) => {
  const [accordionState, setAccordionState] = useState<string[]>()

  const { data: tripBookingsDocuments } = useQuery({
    queryKey: ['trip-bookings-documents', folderId],
    queryFn: async () => {
      const res = await getTripBookingsDocuments(folderId)
      if (res.length > 0) setAccordionState(res.map((_, index) => index.toString()))
      return res
    }
  })

  return (
    <div className="py-3 flex flex-col gap-4">
      <Typography variant="sub-heading">Billets</Typography>

      <div className="flex flex-col gap-4">
        {tripBookingsDocuments && tripBookingsDocuments.length > 0 ? (
          tripBookingsDocuments.map((tripBooking, index) => (
            <Accordion
              key={index}
              id={index.toString()}
              title={
                <Badge
                  className="mr-6 px-2 py-2"
                  size="flexible"
                  variant="white"
                  text={
                    <Typography variant="tag" color={tailwindColors.secondary.DEFAULT}>
                      {transportTypeMapping[tripBooking.directionality]?.label}
                      <span className="text-black">
                        {' - '}
                        {TripBookingProperties.getDetails(tripBooking, {
                          displayTravelCompany: true,
                          displayDate: true
                        })}
                      </span>
                    </Typography>
                  }
                />
              }
              variant="primary"
              open={accordionState?.includes(index.toString())}
              onToggle={() => {
                if (accordionState?.includes(index.toString()))
                  setAccordionState(accordionState?.filter((key) => key !== index.toString()))
                else setAccordionState([...(accordionState ?? []), index.toString()])
              }}
            >
              <div className="flex flex-col gap-2 mt-2">
                {tripBooking.tripSteps.length > 0 && (
                  <div className="bg-white-dark p-2 flex flex-col gap-2 rounded">
                    {tripBooking.tripSteps.map((step, stepIndex) => {
                      const { departurePlace, arrivalPlace, departureTime, arrivalTime } =
                        TripStepProperties.getTripStepDetails(step)
                      return (
                        <div key={stepIndex} className="flex gap-2 items-center">
                          <div className="flex gap-1 items-center">
                            <Typography variant="label-semibold" className="text-vividBlue">
                              {departurePlace}
                            </Typography>
                            <Typography variant="label-semibold">-</Typography>
                            <Typography variant="label-semibold" className="text-vividBlue">
                              {arrivalPlace}
                            </Typography>
                            <Typography variant="label-semibold">:</Typography>
                          </div>
                          <div className="flex gap-1 items-center col-span-2">
                            <Typography variant="label-medium">Départ</Typography>
                            <Badge
                              variant={'grey-light'}
                              borderColor={tailwindColors.grey.light.hover}
                              text={departureTime}
                            />
                            <Typography variant="label-semibold">-</Typography>
                            <Typography variant="label-medium">Arrivée</Typography>
                            <Badge
                              variant={'grey-light'}
                              borderColor={tailwindColors.grey.light.hover}
                              text={arrivalTime}
                            />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                <TripBookingDocumentsDataTable tripBookingId={tripBooking.id} />
              </div>
            </Accordion>
          ))
        ) : (
          <Typography variant="body-medium" color={tailwindColors.grey.light.active}>
            Aucun billet trouvé
          </Typography>
        )}
      </div>
    </div>
  )
}

export default FolderTripBookingDocuments
